import { AfterViewInit, Component, ElementRef, OnInit, TemplateRef, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { TaskDialogComponent, TaskDialogData, TaskDialogResult } from '../../../../shared/components/task-dialog/task-dialog.component';
import { ageFromDob, sexFromPatient, toOnsetDate } from 'src/app/shared/util/icd-coding-context.util';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { EMPTY, Subscription, combineLatest, forkJoin, switchMap, takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { patientGroupId } from './../../../../core/utils/patient-group';
import { AnalyticsService } from './../../../../core/services/analytics.service';
import { IcdCode } from './../../../../core/services/app-data.service';
import { DeceasedAppointmentCheckService } from './../../../../core/services/deceased-appointment-check.service';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { GroupService } from './../../../../core/services/group.service';
import { HospitalService } from './../../../../core/services/hospital.service';
import { PatientAiService } from './../../../../core/services/patient-ai.service';
import { PatientBillingService } from './../../../../core/services/patient-billing.service';
import { PatientCommunicationService } from './../../../../core/services/patient-communication.service';
import { PatientTaskService } from './../../../../core/services/patient-task.service';
import { PatientService } from './../../../../core/services/patient.service';
import { PdfDownloadService } from './../../../../core/services/pdf-download.service';
import { ScrollPositionService } from './../../../../core/services/scroll-position.service';
import { SocketService } from './../../../../core/services/socket.service';
import { MedicationAutocompleteComponent } from './../../../../shared/components/medication-autocomplete/medication-autocomplete.component';
import { UserService } from 'src/app/core/services/user.service';
import { MORBIDITY_MORTALITY_REASONS } from 'src/app/core/data/app.data';

@Component({
  selector: 'app-discharge-details',
  templateUrl: './discharge-details.component.html',
  styleUrls: ['./discharge-details.component.scss']
})
export class DischargeDetailsComponent extends BaseComponent implements OnInit, AfterViewInit {

  // ── ICD-10 coding-rule context (shared/util/icd-coding-context.util.ts) ──────────
  //
  // Feeds the code selector's rules. Each returns null when the value is unknown, and a rule
  // with no context stays SILENT rather than guessing — a false alarm reads as the system not
  // knowing the patient, which costs trust in every other finding.
  get icdCodingSex(): string | null { return sexFromPatient(this.patient); }
  get icdCodingAge(): number | null { return ageFromDob(this.patient?.dob); }

  /** Dates a newly added code from the discharge being captured — never from today. */
  get icdDefaultOnsetDate(): string | null { return toOnsetDate(this.form?.value?.dischargeDate); }


  @ViewChild('medicationAutocomplete') medicationAutocomplete?: MedicationAutocompleteComponent;


  @ViewChild('buildRecipientListDialog') buildRecipientListDialog?: TemplateRef<any>;


  @ViewChild('dischargeSummaryTextarea', { static: false }) dischargeSummaryTextarea?: ElementRef;
  @ViewChild('patientDischargeSummaryTextarea', { static: false }) patientDischargeSummaryTextarea?: ElementRef;
  @ViewChildren(MatMenuTrigger) emailMenuTriggers?: QueryList<MatMenuTrigger>;


  patientTasks: any = [];
  taskListActions: any = [{ icon: 'add', tooltip: 'Add a new task to the patient file', action: (event: MouseEvent) => this.addTask(event) }]

  form!: FormGroup;
  patient: any;
  group: any;
  groupId!: string;
  hospitals: any[] = [];
  patientProcedureCodes: any[] | null = [];
  visibleDiagnosisFields: string[] = [];
  selectedIcdCodes: IcdCode[] = [];
  selectedAllergies: any[] = [];
  selectedMedicalHistory: any[] = [];
  selectedMedicationHistory: any[] = [];
  noAllergies: boolean = false;
  noMedicalHistory: boolean = false;
  noMedicationHistory: boolean = false;

  todayDate: moment.Moment = moment();
  private socketSubs: Subscription[] = [];

  public dialogRef!: MatDialogRef<any>;

  suggestedDischargeTasks: any;
  suggestedDischargeMedication: any;

  userProfile: any;
  canSign: boolean = false;
  isPhysician: boolean = false;
  groupUsers: any[] = [];

  hasSignaturePermission: boolean = false;

  // Morbidity/Mortality fields
  mmReasons: string[] = MORBIDITY_MORTALITY_REASONS;

  private hasCheckedAutoGeneration: boolean = false;


  /** A state unit (tasks/state-mode.md) sends nothing: summaries and prescriptions are downloaded instead. */
  get stateUnit(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private groupService: GroupService,
    private userService: UserService,
    private hospitalService: HospitalService,
    private patientService: PatientService,
    private patientBillingService: PatientBillingService,
    private pdfDownloadService: PdfDownloadService,
    private socketService: SocketService,
    private aiService: PatientAiService,
    private patientCommunicationService: PatientCommunicationService,
    private scrollService: ScrollPositionService,
    private taskService: PatientTaskService,
    private analyticsService: AnalyticsService,
    private deceasedAppointmentCheckService: DeceasedAppointmentCheckService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);

    this.hospitalService.hospitals$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(hospitals => {
        this.hospitals = hospitals;
      });

    this.patientBillingService.patientBilling$
      .pipe(takeUntil(this.destroy$))
      .subscribe((billing: any[] | null) => {
        this.patientProcedureCodes = billing;
      });

    //Getting current user signed
    this.userProfile = this.userService.user;

    this.taskService.patientTasks$
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.patientTasks = result;
      })

  }

  ngOnInit() {
    this.initForm();
    const patientId = this.route.snapshot.paramMap.get('id');
    if (patientId) {
      this.patientService.getPatientById(patientId)
        .pipe(
          takeUntil(this.destroy$)
        ).subscribe(); // Trigger loading the patient
    }

    this.groupService.selectedGroup$
      .pipe(
        switchMap(selectedGroup => {
          if (selectedGroup) {
            this.group = selectedGroup;
            this.groupId = selectedGroup.id;
            this.form.patchValue({ groupId: patientGroupId(this.patient, this.groupId) });
          } else {
            this.group = null;
          }
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      ).subscribe();

    this.patientService.currentPatient$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(patient => {
        if (patient) {
          this.patient = patient;
          // Basic patching for directly matched fields
          this.form.patchValue(this.patient);
          this.form.patchValue({ email: patient.email, name: `${patient.name} ${patient.surname}` });



          const data = this.patient;
          // Patch the diagnosis fields dynamically
          for (let i = 2; i <= 24; i++) {
            const fieldName = `diagnosis${i}`;
            if (data[fieldName]) {
              this.addDiagnosisField();
            }
          }

          if (this.patient.icdCodes) {
            this.onIcdCodesChanged(this.patient.icdCodes);
          }
          if (this.patient.allergies) {
            this.onAllergiesChanged(this.patient.allergies);
          }
          if (this.patient.medicalHistory) {
            this.onMedicalHistoryChanged(this.patient.medicalHistory);
          }
          if (this.patient.medicationHistory) {
            this.onMedicationHistoryChanged(this.patient.medicationHistory);
          }

        } else {
          this.resetForm();
        }
      });

    // Wait for both group and patient to be available before checking auto-generation
    combineLatest([
      this.groupService.selectedGroup$,
      this.patientService.currentPatient$
    ]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(([group, patient]) => {
      // Check if both are available and we haven't checked yet
      if (group && patient && !this.hasCheckedAutoGeneration) {
        // Use setTimeout to ensure view is initialized (in case ngAfterViewInit hasn't run yet)
        setTimeout(() => {
          if (!this.hasCheckedAutoGeneration && group?.autoGenerateSections?.dischargeSummary) {
            this.hasCheckedAutoGeneration = true;
            this.aiGenerateDischargeSummary();
          }
        }, 0);
      }
    });

    this.isPhysician = this.userService.isUserPhysician;
    this.canSign = this.userService.canUserSign;
    this.hasSignaturePermission = this.userService.isNonPhysician;
    this.groupUsers = this.userService.availableDoctorsList;
  }

  ngAfterViewInit() {
    // Check for auto-generation only after view is initialized and both group and patient are available
    // This handles the case where ngAfterViewInit runs after the observables have already emitted
    if (!this.hasCheckedAutoGeneration && this.group && this.patient) {
      this.hasCheckedAutoGeneration = true;
      if (this.group?.autoGenerateSections?.dischargeSummary) {
        this.aiGenerateDischargeSummary();
      }
    }
  }

  resetForm() {
    setTimeout(() => {
      this.patient = null;
      this.selectedIcdCodes = [];
      this.visibleDiagnosisFields = [];
      this.selectedAllergies = [];
      this.selectedMedicalHistory = [];
      this.selectedMedicationHistory = [];
      this.form.reset();
      this.initForm();
    });
  }


  onRecipientSelected(recipient: { name: string, email: string }) {
    this.form.patchValue({
      name: recipient.name,
      email: recipient.email
    });
  }

  initForm() {
    this.form = this.fb.group({
      status: ['opd', Validators.required],
      groupId: [patientGroupId(this.patient, this.groupId), Validators.required],
      dischargeDate: [moment(), Validators.required],
      dischargeSummary: [''],
      patientDischargeSummary: [''],
      dischargeSummaryPrompt: [''],
      patientDischargeSummaryPrompt: [''],
      email: ['', Validators.email],
      ccEmail: ['', Validators.email],
      name: [''],
      diagnosis: ['', Validators.required],
      icdCodes: [[]],
      allergies: [[]],
      medicalHistory: [[]],
      medicationHistory: [[]],
      noAllergies: [false],
      noMedicalHistory: [false],
      noMedicationHistory: [false],
      includeSignatureOnPdf: [false], //The checkbox
      includeSignatureOnEmail: [false],//The checkbox
      selectedDoctor: [null],//The selected doctor object
      pdfDate: [new Date()],//Date shown on the generated PDF; defaults to today, user can override via the download mat-menu

      // Morbidity/Mortality fields
      isMorbidity: [true],
      isMortality: [false],
      mmReason: [''],
      mmNotes: ['']

    });

    // Add diagnosis fields dynamically
    for (let i = 2; i <= 24; i++) {
      const fieldName = `diagnosis${i}`;
      this.form.addControl(fieldName, this.fb.control(''));
    }
  }

  addDiagnosisField(): void {
    const nextIndex = this.visibleDiagnosisFields.length + 2; // Start at 2 for diagnosis2
    if (nextIndex <= 24) {
      const fieldName = `diagnosis${nextIndex}`;
      this.form.addControl(fieldName, this.fb.control(''));
      this.visibleDiagnosisFields.push(fieldName);
    }
  }

  onIcdCodesChanged(codes: IcdCode[]) {
    this.selectedIcdCodes = codes;
    this.form.patchValue({ icdCodes: codes });
  }
  onAllergiesChanged(allergies: any[]) {
    this.selectedAllergies = allergies;
    this.form.patchValue({ allergies: allergies });
  }
  onMedicalHistoryChanged(history: any[]) {
    this.selectedMedicalHistory = history;
    this.form.patchValue({ medicalHistory: history });
  }
  onMedicationHistoryChanged(history: any[]) {
    this.selectedMedicationHistory = history;
    this.form.patchValue({ medicationHistory: history });
  }
  onNoAllergiesChanged(noAllergies: boolean) {
    this.form.patchValue({ noAllergies: noAllergies });
  }
  onNoMedicalHistoryChanged(noMedicalHistory: boolean) {
    this.form.patchValue({ noMedicalHistory: noMedicalHistory });
  }
  onNoMedicationHistoryChanged(noMedicationHistory: boolean) {
    this.form.patchValue({ noMedicationHistory: noMedicationHistory });
  }

  toggleMorbidity(event: any) {
    this.form.patchValue({ isMorbidity: event.checked });
    this.form.patchValue({ isMortality: !event.checked });
  }

  toggleMortality(event: any) {
    this.form.patchValue({ isMortality: event.checked });
    this.form.patchValue({ isMorbidity: !event.checked });
  }

  canAddTask(task: string) {
    return this.patientTasks.filter((t: any) => t.task.toLowerCase() === task.toLowerCase()).length === 0;
  }

  canAddMedication(medication: string) {
    return this.medicationAutocomplete?.medicationList.filter((item: any) => item.name.toLowerCase() === medication.toLowerCase()).length === 0;
  }

  addMedication(event: MouseEvent, medication: any) {
    event.preventDefault();
    event.stopPropagation();
    this.medicationAutocomplete?.addMedicationItem(medication);
  }

  async dischargePatient() {
    const { icdCodes } = this.form.value;
    if (!icdCodes.length) {
      this.showToastMessage('At least one ICD-10 code is required. Please add an ICD-10 code to continue.');
      return;
    }

    const dischargeDate = this.form.value.dischargeDate;
    const dischargeMoment = moment.utc(dischargeDate).startOf('day');
    const todayMoment = moment.utc().startOf('day');

    if (dischargeMoment.isBefore(todayMoment, 'day')) {
      // Start cleaning up codes from the day after the discharge date
      const startCleanupDate = dischargeMoment.clone().add(1, 'day');
      await this.patientBillingService.checkOverCoding({
        startDate: startCleanupDate.format('YYYY-MM-DD'),
        endDate: todayMoment.format('YYYY-MM-DD')
      }, this.hospitals, this.patient, this.patientProcedureCodes, this);
    } else if (dischargeMoment.isSame(todayMoment, 'day')) {
      await this.patientBillingService.checkMissingDischargeCoding(this.patient, { dischargeDate, hospitalId: this.patient.hospitalId, wardId: this.patient.wardId }, this.hospitals, this.patientProcedureCodes, this);
    }

    this.patientService.dischargePatient(this.patient.id, this.form.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          // Read before loadPatient() - it re-patches the form from the server patient.
          const isMortality = !!this.form.value.isMortality;
          const dischargedPatientId = result.data.id;

          this.showToastMessage('Patient discharged successfully');
          this.loadPatient(dischargedPatientId);

          // A deceased patient can still have appointments on the books. Runs after
          // the discharge has completed and navigation is under way, so it never
          // blocks or pre-empts that flow.
          if (isMortality) {
            this.checkForFutureAppointments(dischargedPatientId);
          }
        },
        error: (error) => {
          console.error('Error discharging patient: ', error);
          this.showToastMessage('There was an error discharging the patient. Please try again.');
        }
      });
  }

  /**
   * Offer to deal with the patient's upcoming appointments after a discharge with
   * Mortality ticked. Deliberately not piped through destroy$: loadPatient() has
   * already navigated away from this component and the prompt must outlive it.
   */
  private checkForFutureAppointments(patientId: string) {
    this.deceasedAppointmentCheckService.checkForFutureAppointments(patientId)
      .subscribe(outcome => {
        if (outcome.action === 'review') {
          this.deceasedAppointmentCheckService.scrollToPatientAppointments(patientId);
        }
        // Every other outcome needs no navigation - the discharge flow has already
        // taken the user to the patient file.
      });
  }

  openEmailListCreator() {
    this.dialogRef = this.dialog.open(this.buildRecipientListDialog!, { width: '600px', autoFocus: false });
    return this.dialogRef.afterClosed().toPromise();
  }

  async sendDischargeSummary(sendType: 'patient' | 'doctor') {
    if (sendType === 'doctor') {
      return this.sendDoctorDischargeSummary();
    }
    this.analyticsService.trackButtonClick(`Discharge Send Patient Discharge Summary`);
    let { email, patientDischargeSummary, name } = this.form.value;

    const content = patientDischargeSummary;
    const type = 'Discharge Summary';

    if (!email) {
      this.showToastMessage('An email address was not provided. Please check the relevant email field try again.');
      return;
    }

    const plainTextContent = this.pdfDownloadService.convertToPlainText(content);


    const pdfDate = this.form.get('pdfDate')?.value;

    if (this.form.get('includeSignatureOnEmail')?.value && this.isPhysician) {
      const appendSignatureMessage = plainTextContent;
      this.patientEmailCommunication(type, name, email, appendSignatureMessage, this.form.get('includeSignatureOnEmail')?.value, this.userProfile.signatureImageUrl, `${this.userProfile.title} ${this.userProfile.name} ${this.userProfile.surname}`, pdfDate)

    } else if (!this.isPhysician && this.hasSignaturePermission) {
      const appendSignatureMessage = plainTextContent;
      this.patientEmailCommunication(type, name, email, appendSignatureMessage, this.form.get('includeSignatureOnEmail')?.value, this.form.get('selectedDoctor')?.value.signatureImageUrl, `${this.form.get('selectedDoctor')?.value.title} ${this.form.get('selectDoctor')?.value.name} ${this.form.get('selectedDoctor')?.value.surname}`, pdfDate)

    } else if (!this.form.get('includeSignatureOnEmail')?.value || !this.canSign) {
      this.patientEmailCommunication(type, name, email, content, undefined, undefined, undefined, pdfDate);
    }

  }

  async sendDoctorDischargeSummary() {
    const dialogResult = await this.openEmailListCreator();
    if (!dialogResult) return;
    this.analyticsService.trackButtonClick(`Discharge Send Doctor Discharge Summary`);
    let { dischargeSummary } = this.form.value;
    const requests = []
    for (const recipient of dialogResult) {
      const type = 'Discharge Summary';

      let content = dischargeSummary;
      const plainTextContent = this.pdfDownloadService.convertToPlainText(content);

      const pdfDate = this.form.get('pdfDate')?.value;

      if (this.form.get('includeSignatureOnEmail')?.value && this.isPhysician) {
        const appendSignatureMessage = plainTextContent + '\n\n'
          + `${this.userProfile.title} ${this.userProfile.name} ${this.userProfile.surname}` + '\n';
        requests.push(this.patientCommunicationService.sendAIContentEmail({
          patientId: this.patient.id,
          type: type,
          name: recipient.name,
          email: recipient.email,
          content: appendSignatureMessage,
          speciality: '',
          images: [],
          attachSignature: true,
          signatureUrl: this.userProfile.signatureImageUrl,
          pdfDate: pdfDate,
        }))

      } else if (!this.isPhysician && this.hasSignaturePermission && this.form.get('includeSignatureOnEmail')?.value) {
        const appendSignatureMessage = plainTextContent + '\n\n'
          + `${this.form.get('selectedDoctor')?.value.title} ${this.form.get('selectedDoctor')?.value.name} ${this.form.get('selectedDoctor')?.value.surname}` + '\n';
        requests.push(this.patientCommunicationService.sendAIContentEmail({
          patientId: this.patient.id,
          type: type,
          name: recipient.name,
          email: recipient.email,
          content: appendSignatureMessage,
          speciality: '',
          images: [],
          attachSignature: true,
          signatureUrl: this.form.get('selectedDoctor')?.value.signatureImageUrl,
          pdfDate: pdfDate,
        }))

      } else if (!this.form.get('includeSignatureOnEmail')?.value) {
        requests.push(this.patientCommunicationService.sendAIContentEmail({
          patientId: this.patient.id,
          type: type,
          name: recipient.name,
          email: recipient.email,
          content: plainTextContent,
          speciality: '',
          images: [],
          pdfDate: pdfDate,
        }))
      }
    }
    forkJoin(requests)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Emails sent successfully');
          this.closeEmailMenus();
        },
        error: (error) => {
          console.error('Error sending AI emails: ', error);
          this.showToastMessage('There was an error sending the emails. Please try again.');
        }
      });
  }

  patientEmailCommunication(type: string, name: string, email: string, content: string, allowSigning?: boolean, doctorSignature?: string, signatoryName?: string, pdfDate?: Date | string): void {
    this.patientCommunicationService.sendAIContentEmail({
      patientId: this.patient.id,
      type: type,
      name: name,
      email: email,
      content: content,
      speciality: '',
      images: [],
      attachSignature: allowSigning,
      signatureUrl: doctorSignature,
      signatoryName: signatoryName,
      pdfDate: pdfDate,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Email sent successfully');
          this.closeEmailMenus();
        },
        error: (error) => {
          console.error('Error sending AI email: ', error);
          this.showToastMessage('There was an error sending the email. Please try again.');
        }
      });
  }

  aiGenerateDischargeSummary() {
    this.analyticsService.trackButtonClick(`Discharge Generate Discharge Summary`);
    this.scrollService.scrollTo('details')
    this.subscribeToOutputUpdates()
    const { dischargeDate } = this.form.value;
    const admissionDate = moment.isMoment(this.patient.admissionDate) ? this.patient.admissionDate : moment(this.patient.admissionDate, 'DD/MM/YYYY');
    this.aiService.generateDischargeSummary(this.patient.id, admissionDate.format('DD/MM/YYYY'), dischargeDate.format('DD/MM/YYYY'), this.form.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  aiGenerateMorbidityMortality() {
    this.analyticsService.trackButtonClick(`Discharge Generate Morbidity Mortality`);
    this.subscribeToMMOutputUpdates();
    const { dischargeDate } = this.form.value;
    const admissionDate = moment.isMoment(this.patient.admissionDate) ? this.patient.admissionDate : moment(this.patient.admissionDate, 'DD/MM/YYYY');
    this.aiService.generateMorbidityMortality(this.patient.id, admissionDate.format('DD/MM/YYYY'), dischargeDate.format('DD/MM/YYYY'), this.form.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  private subscribeToOutputUpdates() {
    let outputStream = '';
    let patientOutputStream = '';

    if (this.socketSubs) {
      for (const sub of this.socketSubs)
        sub.unsubscribe();
      outputStream = '';
      patientOutputStream = '';
    }

    const dischargeSub = this.socketService.listen('aiOutputUpdate-dischargeSummary', 'discharge-details-component')
      .pipe(takeUntil(this.destroy$))
      .subscribe(output => {
        outputStream += output.results || '';
        this.form.patchValue({ dischargeSummary: outputStream });
        setTimeout(() => {
          this.dischargeSummaryTextarea!.nativeElement.scrollTop = this.dischargeSummaryTextarea!.nativeElement.scrollHeight;
        }, 0);
        if (output.completed) {
          dischargeSub.unsubscribe();
        }
      });

    const patientDischargeSub = this.socketService.listen('aiOutputUpdate-patientDischargeSummary', 'discharge-details-component')
      .pipe(takeUntil(this.destroy$))
      .subscribe(output => {
        patientOutputStream += output.results || '';
        this.form.patchValue({ patientDischargeSummary: patientOutputStream });
        setTimeout(() => {
          this.patientDischargeSummaryTextarea!.nativeElement.scrollTop = this.patientDischargeSummaryTextarea!.nativeElement.scrollHeight;
        }, 0);
        if (output.completed) {
          patientDischargeSub.unsubscribe();
        }
      });


    const dischargeTasksSub = this.socketService.listen('aiOutputUpdate-dischargeTasks', 'discharge-details-component')
      .pipe(takeUntil(this.destroy$))
      .subscribe(output => {
        this.suggestedDischargeTasks = output.results
        if (output.completed) {
          dischargeTasksSub.unsubscribe();
        }
      });


    const dischargeMedicationSub = this.socketService.listen('aiOutputUpdate-dischargeMedication', 'discharge-details-component')
      .pipe(takeUntil(this.destroy$))
      .subscribe(output => {
        this.suggestedDischargeMedication = output.results
        // Parse the item field for each medication in suggestedDischargeMedication
        if (this.suggestedDischargeMedication?.medication?.length) {
          this.suggestedDischargeMedication.medication = this.suggestedDischargeMedication.medication.map((med: any) => {
            // Only parse if item is a string, otherwise it's already an object
            if (med.item && typeof med.item === 'string') {
              try {
                med.item = JSON.parse(med.item);
              } catch (error: any) {
                console.error('Error parsing item:', med.item, 'Error:', error.message);
              }
            }
            return med;
          });
        }

        if (output.completed) {
          dischargeMedicationSub.unsubscribe();
        }
      });

    this.socketSubs.push(
      this.socketService.listen('aiOutputError', 'discharge-details-component')
        .subscribe(error => {
          console.error('AI output generation error:', error);
          // Notify user of error
          alert('Error generating AI output.');
        })
    );
  }

  private subscribeToMMOutputUpdates() {
    let mmOutputStream = '';

    if (this.socketSubs) {
      for (const sub of this.socketSubs)
        sub.unsubscribe();
      mmOutputStream = '';
    }

    const mmSub = this.socketService.listen('aiOutputUpdate-morbidityMortality', 'discharge-details-component')
      .pipe(takeUntil(this.destroy$))
      .subscribe(output => {
        mmOutputStream += output.results || '';
        if (mmOutputStream) {
          this.form.patchValue({ mmNotes: mmOutputStream });
        }
        if (output.completed) {
          mmSub.unsubscribe();
        }
      });

    const mmReasonSub = this.socketService.listen('aiOutputUpdate-morbidityMortalityReason', 'discharge-details-component')
      .pipe(takeUntil(this.destroy$))
      .subscribe(output => {
        console.log('mmReason output:', output);
        if (output.results) {
          this.form.patchValue({ mmReason: output.results });
        }
        if (output.completed) {
          mmReasonSub.unsubscribe();
        }
      });

    this.socketSubs.push(
      this.socketService.listen('aiOutputError', 'discharge-details-component')
        .subscribe(error => {
          console.error('AI output generation error:', error);
          // Notify user of error
          alert('Error generating AI output.');
        })
    );
  }

  aiPerformUserPrompt(event: Event | null, type: 'dischargeSummary' | 'patientDischargeSummary') {
    this.analyticsService.trackButtonClick(`Discharge User Prompt For Discharge Summary`);
    event?.preventDefault();
    event?.stopPropagation();
    if ((event as KeyboardEvent)?.shiftKey) return;
    const form = this.form;


    const prompt = form.get(`${type}Prompt`)?.value
    if (!prompt) return;

    const { dischargeSummary, patientDischargeSummary, dischargeDate } = form.value;
    const admissionDate = this.patient.admissionDate;


    form.get(`${type}Prompt`)?.patchValue('');
    form.get(`${type}Prompt`)?.markAsPristine();

    this.subscribeToOutputUpdates();
    form.get(`${type}`)?.patchValue('');
    this.aiService.performUserDischargePrompt(this.patient.id, admissionDate, dischargeDate, dischargeSummary, patientDischargeSummary, prompt, type)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  downloadAsPdf(saveType: 'doctor' | 'patient') {
    this.analyticsService.trackButtonClick(`Discharge Download PDF For ${saveType}`);
    const content = saveType === 'doctor' ? this.form.get('dischargeSummary')?.value : this.form.get('patientDischargeSummary')?.value;
    if (content[0].trim()) {
      const filename = `MedicAI_${this.patient.name} ${this.patient.surname}_Discharge Summary_${moment().valueOf()}`;

      const pdfDate = this.form.get('pdfDate')?.value;

      if (this.form.get('includeSignatureOnPdf')?.value && this.isPhysician) {
        const appendSignatureMessage = content;

        this.pdfDownloadService.downloadPdf({
          content: appendSignatureMessage,
          filename: filename,
          patient: this.patient,
          images: [],
          attachSignature: true,
          signatureUrl: this.userProfile.signatureImageUrl,
          signatoryName: `${this.userProfile.title} ${this.userProfile.name} ${this.userProfile.surname}`,
          date: pdfDate,
        });
      } else if (!this.isPhysician && this.hasSignaturePermission) {
        const appendSignatureMessage = content;

        this.pdfDownloadService.downloadPdf({
          content: appendSignatureMessage,
          filename: filename,
          patient: this.patient,
          images: [],
          attachSignature: true,
          signatureUrl: this.form.get('selectedDoctor')?.value.signatureImageUrl,
          signatoryName: `${this.form.get('selectedDoctor')?.value.title} ${this.form.get('selectedDoctor')?.value.name} ${this.form.get('selectedDoctor')?.value.surname}`,
          date: pdfDate,
        });
      } else if (!this.form.get('includeSignatureOnPdf')?.value) {
        this.pdfDownloadService.downloadPdf({
          content: content,
          filename: filename,
          patient: this.patient,
          images: [],
          attachSignature: false,
          date: pdfDate,
        });

      }
    }
  }

  loadPatient(patientId: string | null) {
    this.patientService.getPatientById(patientId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(
        result => {
          if (!patientId) {
            this.router.navigate(['/patients', {
              outlets: {
                patient: null,
              }
            }]);
            return;

          }
          this.router.navigate(['/patients', {
            outlets: {
              patient: ['file', patientId],
            }
          }]);
        }, error => {
          console.error('Error loading patient: ', error);
          this.showToastMessage('There was an error loading the patient. Please try again.');
        });
  }

  openAddTaskDialog(content?: any): Promise<TaskDialogResult | null | undefined> {
    const data: TaskDialogData = content
      ? { date: moment(content.date), task: content.task, dueDate: content.dueDate || '' }
      : {};
    return this.dialog.open<TaskDialogComponent, TaskDialogData, TaskDialogResult | null>(TaskDialogComponent, { width: '600px', data })
      .afterClosed().toPromise();
  }

  async addTask(event: MouseEvent, taskValue?: any) {
    event.preventDefault();
    event.stopPropagation();

    taskValue = taskValue?.task || '';

    const dialogResult = await this.openAddTaskDialog({ date: moment(), task: taskValue });

    if (!dialogResult) return;

    const { date, task, notes, dueDate, assigneeUserIds } = dialogResult;

    if (!date || !task) {
      this.showToastMessage('Date and Task are required to add a task');
      return
    };

    const taskData = {
      patientId: this.patient.id,
      groupId: this.patient.groupId,
      date: moment.utc(date).startOf('day').toISOString(),
      dueDate: moment.utc(dueDate).startOf('day').toISOString(),
      task,
      notes,
      assigneeUserIds,
      status: 'Pending'
    }

    this.taskService.addTask(taskData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Patient task saved successfully');
        },
        error: (error) => {
          console.error('Error saving patient task: ', error);
          this.showToastMessage('There was an error saving the patient task. Please try again.');
        }
      });
  }

  async savePrescription() {
    const isValid = this.medicationAutocomplete?.isValid();
    if (!isValid) {
      this.showToastMessage('At least one medication item is required to create a prescription. Please try again.');
      return;
    }
    const saved = await this.medicationAutocomplete?.downloadPrescription();
    if (saved) {
      this.showToastMessage('Prescription created successfully.');
      this.medicationAutocomplete?.resetState();
    }
  }

  async emailPrescription() {
    const isValid = this.medicationAutocomplete?.isValid();
    if (!isValid) {
      this.showToastMessage('At least one medication item is required to create a prescription. Please try again.');
      return;
    }
    const { email, ccEmail } = this.form.value
    const sent = await this.medicationAutocomplete?.emailPrescription(email, ccEmail, true);
    if (sent) {
      this.showToastMessage('Prescription sent successfully.');
      this.medicationAutocomplete?.resetState();
      this.closeEmailMenus();
    }
  }

  returnToParent() {
    if (this.patient && this.patient.id) {
      this.router.navigate(['/patients', { outlets: { patient: ['file', `${this.patient.id}`] } }]);
    } else {
      this.router.navigate(['/patients', { outlets: { patient: null } }]);
    }
  }

  handleMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      event.stopPropagation();
    }
  }

  closeEmailMenus(): void {
    if (this.emailMenuTriggers) {
      this.emailMenuTriggers.forEach(trigger => {
        if (trigger.menuOpen) {
          trigger.closeMenu();
        }
      });
    }
  }

  /**
   * Get plain text for discharge summary (for WhatsApp)
   */
  getDischargeSummaryPlainText(): string {
    const summaryContent = this.form.get('dischargeSummary')?.value;
    return summaryContent ? this.pdfDownloadService.convertToPlainText(summaryContent) : '';
  }

  /**
   * Get plain text for patient discharge summary (for WhatsApp)
   */
  getPatientDischargeSummaryPlainText(): string {
    const summaryContent = this.form.get('patientDischargeSummary')?.value;
    return summaryContent ? this.pdfDownloadService.convertToPlainText(summaryContent) : '';
  }
}
